import { useQuery } from '@tanstack/react-query'
import $api, { API_URL } from '../../../../../API'
import { Skeleton, Table, Tooltip } from 'antd'
import { useCallback, useMemo } from 'react'
import type { ColumnsType } from 'antd/es/table'
import * as constants from '../constants'
import cx from 'classnames'
import type { X937FileChecksResponse, X937FileExpandableRowProps } from '../types'
import type { X937FileCheck } from '../../../../../domain/x937'
import { FileImageOutlined } from '@ant-design/icons'
import { useAppDispatch } from '../../../../../store'
import * as previewActions from '../../../../../store/previewer'
import type { Attachment } from '../../../../../store/previewer/types'

export const X937FileExpandableRow = (props: X937FileExpandableRowProps): JSX.Element => {
  const x937FileChecksQuery = useQuery({
    queryKey: ['x937-file-checks', { fileId: props.fileId }],

    async queryFn({ queryKey, signal }) {
      const { fileId } = queryKey[1] as typeof queryKey extends Array<string | infer T> ? T : never

      const response = await $api.get<X937FileChecksResponse>(
        `/pinnacle/x937/files/${fileId}/checks`,
        {
          signal
        }
      )

      return response.data
    }
  })

  const dispatch = useAppDispatch()

  const checks = x937FileChecksQuery.data ?? []

  const handlePdfPreviewClick = useCallback(
    (file: Attachment) => (): void => {
      dispatch(previewActions.open(file))
    },
    [dispatch]
  )

  const handlePropagatedActionClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      event.stopPropagation()
    },
    []
  )

  const token = window.localStorage.getItem('token')

  const buildFileHref = useCallback(
    (checkId: string) => {
      return `${API_URL}/pinnacle/x937/checks/${checkId}/images?token=${encodeURIComponent(token ?? '')}`
    },
    [token]
  )

  const columns = useMemo<ColumnsType<X937FileCheck>>(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 300,

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Check Number',
        dataIndex: 'checkNumber',
        key: 'checkNumber',

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Routing Number',
        dataIndex: 'routingNumber',
        key: 'routingNumber',

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Account Number',
        dataIndex: 'accountNumber',
        key: 'accountNumber',

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Amount',
        dataIndex: 'amountCents',
        key: 'amountCents',
        align: 'right',

        render: (value: number) => {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(value / 100)
        },

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        key: 'actions',
        title: 'Actions',
        align: 'center',
        width: 150,
        render: (_value, record) => (
          <div
            className='flex gap-2 justify-center items-center'
            onClick={handlePropagatedActionClick}
          >
            <Tooltip title='View Checks'>
              <button
                type='button'
                onClick={handlePdfPreviewClick({
                  mimeType: 'image/tiff',
                  name: 'Check images',
                  href: buildFileHref(record.id)
                })}
              >
                <FileImageOutlined className='text-lg text-blue-500 cursor-pointer hover:opacity-75' />
              </button>
            </Tooltip>
          </div>
        ),

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      }
    ],
    [buildFileHref, handlePdfPreviewClick, handlePropagatedActionClick]
  )

  return (
    <div className='relative !p-0'>
      <div className='absolute top-0 left-0 z-10 w-1 h-full bg-blue-200' />

      <div className='p-4'>
        <Table
          size='small'
          rowKey='id'
          className='border-2 border-dashed border-blue-200 [&_.ant-table]:![margin-inline:_0]'
          columns={
            !x937FileChecksQuery.isLoading
              ? columns
              : columns.map(column => {
                  return {
                    ...column,
                    render: function renderPlaceholder() {
                      return <Skeleton key={column.key} title active={true} paragraph={false} />
                    }
                  }
                })
          }
          dataSource={
            !x937FileChecksQuery.isLoading
              ? checks
              : ([...Array(2)].map((_, index) => ({
                  id: `key${index}`
                })) as unknown as X937FileCheck[])
          }
          pagination={false}
        />
      </div>
    </div>
  )
}
